import * as React from "react"
import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { Alert, AlertTitle, Grid } from "@mui/material"
import { number, object, string } from "yup"
import { useAppToast, useValidatorSeeker } from "@ui-seeker/app"
import { DrawerSeeker } from "@ui-seeker/core"
import { isEmpty, translateCountryName } from "@ui-seeker/utils"

import { DIPCountry, DIPSite } from "@components"
import { BACKEND_APIS } from "@constants"
import { customerSelectedID } from "@signals"
import { usePricingConfigDraft } from "../../@hooks"
import { mainSiteFormFilters } from "../../@signals"


/** Pick the main site of one market.
 *
 * Both selectors are the **by-customer** variants of v1's site options
 * (`site/market-customer-options`, `site/customer-options`): they take `customerID`, so they answer
 * for the customer being configured instead of the logged-in admin. Same pair
 * `Management/KeywordsCrud` uses. The scope is `DIPI_CUSTOMER_SITE` with `deleted = 0`
 * (`get_id_sites_by_customer`).
 *
 * ⚠️ A configured main site can sit **outside** that scope: 6 of the 45 rows do today, and one of
 * them is in use (customer 17, `Siman.com (CR)` — its own store, not one of its monitored sites). The
 * selector cannot offer it, and `AutocompleteAjaxSeeker` silently blanks a value that is not among
 * the options, so the warning below compares against the **row as loaded**, not against the form.
 */
export default function MainSiteDrawer() {

    const { showToast } = useAppToast()
    const { stageMainSite } = usePricingConfigDraft()
    const form = useSignal({})
    const loadedSiteIDs = useSignal([])

    useEffect(() => {
        form.value = {
            ...mainSiteFormFilters.value,
            // Lowercase on the way in, because that is the case the selector's options use:
            // `market-customer-options` builds them as `value=market.lower()`, `label=market`
            // (v1 `master_data/nested/site/services.py:25`), while the table stores uppercase.
            // Feed it `ES` and the value is not among the options, so `AutocompleteAjaxSeeker`
            // blanks it, fires `onChange` empty and takes `siteID` down with it (see below) —
            // the row opens with both fields empty. It goes back uppercase on save.
            country: mainSiteFormFilters.value?.country?.toLowerCase()
        }
        loadedSiteIDs.value = []
    }, [mainSiteFormFilters.value])

    const isOpen = !isEmpty(mainSiteFormFilters.value)

    const configuredRow = mainSiteFormFilters.value
    const isConfiguredSiteOutOfScope = !!configuredRow?.siteID
        && !!loadedSiteIDs.value.length
        && !loadedSiteIDs.value.includes(configuredRow.siteID)

    const validatorData = useValidatorSeeker({
        country: form.value?.country,
        siteID: form.value?.siteID
    }, object({
        country: string().required('One market must be selected').nullable(),
        siteID: number().required('One main site must be selected').nullable()
    }))

    const drawerProps = {
        title: form.value?.isCreateMode
            ? 'Configure a market'
            // The form holds the market lowercase (see above), so print the country name rather
            // than the raw code: `translateCountryName` uppercases internally and knows `ES_CA`.
            : `Main site of ${translateCountryName(form.value?.country)}`,
        isOpen,
        controller: () => mainSiteFormFilters.value = {},
        successProps: {
            onClick: isEmpty(validatorData.errors)
                ? () => {
                    stageMainSite({
                        // Uppercase on the way out, like the RRSP drawer does with `market`:
                        // `market-customer-options` answers lowercase while the table stores
                        // uppercase, and `stageMainSite` matches the existing row with `===`, so a
                        // lowercase market would stage a *second* row for a market already there.
                        // Only here, not in `onChange`: the raw value is what filters
                        // `site/customer-options` below.
                        country: form.value.country?.toUpperCase(),
                        siteID: form.value.siteID,
                        siteName: form.value.siteName
                    })
                    mainSiteFormFilters.value = {}
                }
                : () => showToast.warning('Please ensure all mandatory fields are completed')
        }
    }

    return <DrawerSeeker {...drawerProps}>
        <Grid container spacing={2}>

            <Grid item xs={12}>
                <DIPCountry
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/site/market-customer-options`}
                    filters={{ customerID: customerSelectedID.value }}
                    value={form.value?.country ?? null}
                    // A market change invalidates the site: `site/customer-options` is filtered by
                    // country, so keeping the old id would show a site outside the new market.
                    onChange={e => form.value = {
                        ...form.value, country: e.target.value, siteID: null, siteName: null
                    }}
                    multiple={false}
                    sx={{ mt: 3, mb: 1 }}
                    error={isOpen && validatorData.errors?.country}
                />
            </Grid>

            <Grid item xs={12}>
                <DIPSite
                    urlAjax={`${BACKEND_APIS.MASTER_DATA}/site/customer-options`}
                    filters={{
                        customerID: customerSelectedID.value,
                        country: form.value?.country
                    }}
                    label="Main site"
                    placeholder="Select the reference site..."
                    value={form.value?.siteID ?? null}
                    onChange={(e, option) => form.value = {
                        ...form.value, siteID: e.target.value, siteName: option?.label
                    }}
                    // `onAjax`, not `onFinishAjax`: DIPSite builds its own option list in
                    // `onFinishAjax`, so overriding that one would leave the selector empty.
                    onAjax={options => {
                        loadedSiteIDs.value = options?.map(option => option.value) ?? []
                        return options
                    }}
                    multiple={false}
                    waiting={!form.value?.country}
                    sx={{ mb: 1 }}
                    error={isOpen && validatorData.errors?.siteID}
                />
            </Grid>

            {!!isConfiguredSiteOutOfScope && <Grid item xs={12}>
                <Alert severity="warning" variant="outlined">
                    <AlertTitle>The current main site is not selectable</AlertTitle>
                    <strong>{configuredRow.siteName}</strong> (#{configuredRow.siteID}) is configured
                    for {configuredRow.country}, but it is <strong>not among this customer's
                    monitored sites</strong>, so this selector cannot offer it — and saving will
                    replace it with whatever you pick here. Close the drawer to leave it as it is.
                </Alert>
            </Grid>}

        </Grid>
    </DrawerSeeker>
}
